// @ts-nocheck
import React from 'react';
import Icon, { ICONS } from '../icons';
import { useLocalization, type Language } from '../../../config/localization';
import type { AssetMark } from '../../../utils/markGeneration';

// Re-export so existing consumers importing AssetMark from MarkButtons keep working,
// while markGeneration.ts stays the single source of truth for the union.
export type { AssetMark };

interface MarkButtonsProps {
  mark?: AssetMark | null;
  onMark: (next: AssetMark | null) => void;
  disabled?: boolean;
  language?: Language;
  size?: number;
  className?: string;
}

// Per-mark icon + active color. Inactive buttons are muted; the active one is
// colored (like=green, dislike=red, approved=blue) — mirrors the radio-style
// toggle described in the gallery asset-marks plan.
const MARK_META: Record<AssetMark, { icon: string; activeClass: string }> = {
  like: { icon: ICONS.THUMBS_UP, activeClass: 'text-green-400 bg-green-500/20' },
  dislike: { icon: ICONS.THUMBS_DOWN, activeClass: 'text-red-400 bg-red-500/20' },
  approved: { icon: ICONS.CHECK, activeClass: 'text-blue-400 bg-blue-500/20' },
};

const MARK_ORDER: AssetMark[] = ['like', 'dislike', 'approved'];

const FALLBACK_LABELS: Record<AssetMark, string> = {
  like: 'Good',
  dislike: 'Bad',
  approved: 'Approved',
};

export default function MarkButtons({
  mark = null,
  onMark,
  disabled = false,
  language = 'en',
  size = 16,
  className = '',
}: MarkButtonsProps) {
  const t = useLocalization(language);

  const handleClick = (value: AssetMark) => (e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabled) return;
    // Clicking the active value toggles it off (null); another value replaces it.
    onMark(mark === value ? null : value);
  };

  return (
    <div className={`flex items-center gap-1 ${className}`} role="group">
      {MARK_ORDER.map((value) => {
        const meta = MARK_META[value];
        const isActive = mark === value;
        const label = t.gallery?.mark?.[value] || FALLBACK_LABELS[value];
        return (
          <button
            key={value}
            type="button"
            onClick={handleClick(value)}
            disabled={disabled}
            aria-label={label}
            aria-pressed={isActive}
            title={label}
            className={`p-1 rounded transition-colors ${
              isActive ? meta.activeClass : 'text-gray-400 hover:text-white hover:bg-white/10'
            } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          >
            <Icon name={meta.icon} size={size} className="text-current" />
          </button>
        );
      })}
    </div>
  );
}
